// Safe arithmetic expression evaluator for "calculated" fields.
// ESM port of cfai-backend/formLogic/expressionEvaluator.js — keep these in sync.
// Supports + - * / % ^ (), unary minus, numbers and identifiers (variable lookups).
// Deliberately does NOT use eval()/new Function() — expressions come from user-authored
// forms and must not be able to execute arbitrary JS.

export function tokenize(expr) {
    const tokens = []
    let i = 0
    while (i < expr.length) {
        const ch = expr[i]
        if (/\s/.test(ch)) { i++; continue }
        if (/[0-9.]/.test(ch)) {
            let j = i
            while (j < expr.length && /[0-9.]/.test(expr[j])) j++
            const raw = expr.slice(i, j)
            if (!/^\d*\.?\d+$/.test(raw)) throw new Error(`Invalid number literal: "${raw}"`)
            tokens.push({ type: "number", value: parseFloat(raw) })
            i = j
            continue
        }
        if (/[A-Za-z_]/.test(ch)) {
            let j = i
            while (j < expr.length && /[A-Za-z0-9_]/.test(expr[j])) j++
            tokens.push({ type: "identifier", value: expr.slice(i, j) })
            i = j
            continue
        }
        if ("+-*/%^()".includes(ch)) {
            tokens.push({ type: "op", value: ch })
            i++
            continue
        }
        throw new Error(`Unexpected character in expression: "${ch}"`)
    }
    return tokens
}

// Grammar (lowest to highest precedence):
//   expr   := term (("+" | "-") term)*
//   term   := power (("*" | "/" | "%") power)*
//   power  := unary ("^" unary)*
//   unary  := "-" unary | primary
//   primary:= number | identifier | "(" expr ")"
export function parse(tokens) {
    let pos = 0

    function peek() { return tokens[pos] }
    function consume(expected) {
        const token = tokens[pos]
        if (!token || (expected && !(token.type === "op" && token.value === expected))) {
            throw new Error(`Unexpected token at position ${pos}: ${JSON.stringify(token)}`)
        }
        pos++
        return token
    }

    function parseExpr() {
        let node = parseTerm()
        while (peek() && peek().type === "op" && (peek().value === "+" || peek().value === "-")) {
            const op = consume().value
            node = { type: "binary", op, left: node, right: parseTerm() }
        }
        return node
    }

    function parseTerm() {
        let node = parsePower()
        while (peek() && peek().type === "op" && ["*", "/", "%"].includes(peek().value)) {
            const op = consume().value
            node = { type: "binary", op, left: node, right: parsePower() }
        }
        return node
    }

    function parsePower() {
        let node = parseUnary()
        while (peek() && peek().type === "op" && peek().value === "^") {
            consume()
            node = { type: "binary", op: "^", left: node, right: parseUnary() }
        }
        return node
    }

    function parseUnary() {
        if (peek() && peek().type === "op" && peek().value === "-") {
            consume()
            return { type: "unary", op: "-", operand: parseUnary() }
        }
        return parsePrimary()
    }

    function parsePrimary() {
        const token = peek()
        if (!token) throw new Error("Unexpected end of expression")
        if (token.type === "number") {
            consume()
            return { type: "number", value: token.value }
        }
        if (token.type === "identifier") {
            consume()
            return { type: "identifier", name: token.value }
        }
        if (token.type === "op" && token.value === "(") {
            consume("(")
            const node = parseExpr()
            consume(")")
            return node
        }
        throw new Error(`Unexpected token: ${JSON.stringify(token)}`)
    }

    const ast = parseExpr()
    if (pos !== tokens.length) throw new Error(`Unexpected trailing tokens at position ${pos}`)
    return ast
}

function evaluateAst(node, scope) {
    switch (node.type) {
        case "number":
            return node.value
        case "identifier": {
            const val = scope[node.name]
            return typeof val === "number" && !isNaN(val) ? val : 0
        }
        case "unary":
            return -evaluateAst(node.operand, scope)
        case "binary": {
            const left = evaluateAst(node.left, scope)
            const right = evaluateAst(node.right, scope)
            switch (node.op) {
                case "+": return left + right
                case "-": return left - right
                case "*": return left * right
                case "/": return right === 0 ? 0 : left / right
                case "%": return right === 0 ? 0 : left % right
                case "^": return Math.pow(left, right)
                default: throw new Error(`Unknown operator: ${node.op}`)
            }
        }
        default:
            throw new Error(`Unknown AST node type: ${node.type}`)
    }
}

// Evaluates `expression` against `scope` (a flat object of variable name -> number).
// Throws on syntax errors. Unknown identifiers evaluate to 0 rather than throwing,
// so a calculated field referencing a not-yet-answered question degrades gracefully.
export function evaluateExpression(expression, scope = {}) {
    const tokens = tokenize(expression)
    const ast = parse(tokens)
    return evaluateAst(ast, scope)
}
